import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Wallet, Banknote, Receipt, Scale, RefreshCw, ArrowRight, X, BarChart3, PieChart as PieIcon, Truck, Building2, Users, UserRound, Route, ChevronUp, ChevronDown } from 'lucide-react';
import { Input, Select } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { LoadError } from '../../components/common/LoadError';
import { ExportFinanceButtons } from '../../components/finance/ExportFinanceButtons';
import { TendenciaMensual, Reparto, RankingResultado, BarraComparada } from '../../components/finance/FinanceCharts';
import { plegar, PALETA_CATEGORICA, SERIE } from '../../utils/financeCharts';
import { useFinanceAnalytics } from '../../hooks/useFinanceAnalytics';
import { useExpenses } from '../../hooks/useExpenses';
import { RankedBarChart } from '../../components/charts/Charts';
import { expensesByDriver } from '../../utils/dashboardStats';
import { useRevenues } from '../../hooks/useRevenues';
import { ingresosPorRuta, type RutaMetrica } from '../../utils/financeRoutes';
import { useVehicles } from '../../hooks/useVehicles';
import { useCustomers } from '../../hooks/useCustomers';
import { useAuth } from '../../context/AuthContext';
import { getSubunits } from '../../lib/api/endpoints';
import { DATE_FILTERS, rangoDeFiltro, type DateFilterId } from '../../utils/dateFilters';
import { formatearCOP, colorDeCategoria } from '../../utils/expenses';
import { FINANCE_GROUPS, aISODate, formatearCantidad, formatearResultado, puedeGestionarTarifas, rangoDeDias } from '../../utils/finance';
import type { FinanceGroupBy, FinanceSummaryRow } from '../../types/finance';
import { NAV_ROUTES } from '../../utils/constants';

/**
 * FE-32 — Finanzas: el resultado del periodo (ingresos − gastos) y su
 * análisis por tiempo, vehículo, sub-unidad, categoría y cliente.
 *
 * Todas las cifras vienen de `GET /finance/summary`, que lee los AGREGADOS del
 * servidor (ADR-016): esta página no suma nada. Es lo que hace que cuadre con
 * el XLSX exportado —lo genera el mismo motor— y con la pestaña Gastos del
 * Dashboard.
 *
 * Interacción: los filtros de arriba (periodo, vehículo, sub-unidad, cliente)
 * acotan TODO (tiles, gráficos, tabla, exportación). Y lo que se ve también
 * filtra: un clic en una barra de vehículo, una porción de cliente o una fila
 * de la tabla pone ese filtro; un clic en un mes de la tendencia abre ese mes.
 *
 * Dos lecturas que la página hace visibles y el cuaderno escondía:
 *  - «Gastos del vehículo» y «de operación» van separados; por vehículo, la
 *    nómina no se resta (ADR-030 §4).
 *  - el resultado puede ser negativo, y se pinta en rojo.
 */
type Periodo = DateFilterId | 'mes' | 'rango';
type Orden = { col: keyof FinanceSummaryRow | 'label'; asc: boolean };

export default function FinancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState<Periodo>('month');
  const [mes, setMes] = useState<string>(() => aISODate(new Date()).slice(0, 7));
  const [rangoDesde, setRangoDesde] = useState(() => aISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [rangoHasta, setRangoHasta] = useState(() => aISODate(new Date()));
  const [vehicleId, setVehicleId] = useState('');
  const [subunitId, setSubunitId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [groupBy, setGroupBy] = useState<FinanceGroupBy>('period');
  const [orden, setOrden] = useState<Orden>({ col: 'label', asc: true });

  const { vehicles } = useVehicles();
  const { customers } = useCustomers();
  const [subunits, setSubunits] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    document.title = 'Finanzas | Paitron Radar';
    // El Supervisor no lista sub-unidades (solo ve la suya): se calla el error.
    getSubunits().then(r => setSubunits(r.items.map(s => ({ id: s.id, name: s.name })))).catch(() => setSubunits([]));
  }, []);

  // El filtro del Dashboard devuelve ISO UTC; el resumen pide DÍAS locales.
  const { desde, hasta } = useMemo(() => {
    if (periodo === 'mes') {
      const [y, m] = mes.split('-').map(Number);
      if (!y || !m) return { desde: aISODate(new Date()), hasta: aISODate(new Date()) };
      return { desde: aISODate(new Date(y, m - 1, 1)), hasta: aISODate(new Date(y, m, 0)) };
    }
    if (periodo === 'rango') {
      const ok = /^\d{4}-\d{2}-\d{2}$/;
      const d = ok.test(rangoDesde) ? rangoDesde : aISODate(new Date());
      const h = ok.test(rangoHasta) && rangoHasta >= d ? rangoHasta : d;
      return { desde: d, hasta: h };
    }
    const r = rangoDeFiltro(periodo);
    const fin = r.to ? new Date(r.to) : new Date();
    const inicio = r.from ? new Date(r.from) : new Date(2020, 0, 1);
    return { desde: aISODate(inicio), hasta: aISODate(fin) };
  }, [periodo, mes, rangoDesde, rangoHasta]);

  const { data, isLoading, error, reload } = useFinanceAnalytics({
    from: desde, to: hasta,
    vehicleId: vehicleId || undefined, subunitId: subunitId || undefined, customerId: customerId || undefined,
  });

  // Gasto por conductor: el agregado no tiene ese grano (ADR-030 §5), así que
  // se lee del detalle de gastos del mismo rango y los mismos filtros. El
  // cliente no aplica (los gastos no tienen cliente).
  const filtroGastos = useMemo(() => ({ ...rangoDeDias(desde, hasta), vehicleId: vehicleId || undefined, subunitId: subunitId || undefined }), [desde, hasta, vehicleId, subunitId]);
  const { expenses } = useExpenses(filtroGastos);
  const porConductor = useMemo(() => expensesByDriver(expenses, 8), [expenses]);
  const pesosNum = (v: number) => formatearCOP(String(v));

  // Rutas: del detalle de ingresos del mismo rango y filtros (ver financeRoutes.ts).
  const filtroIngresos = useMemo(() => ({ ...rangoDeDias(desde, hasta), vehicleId: vehicleId || undefined, subunitId: subunitId || undefined, customerId: customerId || undefined }), [desde, hasta, vehicleId, subunitId, customerId]);
  const { revenues } = useRevenues(filtroIngresos);
  const [ordenRutas, setOrdenRutas] = useState<{ col: keyof RutaMetrica; asc: boolean }>({ col: 'ingresosNum', asc: false });
  const rutas = useMemo(() => {
    const base = ingresosPorRuta(revenues);
    const val = (r: RutaMetrica) => r[ordenRutas.col];
    return base.sort((a, b) => {
      const x = val(a), y = val(b);
      const cmp = typeof x === 'string' && typeof y === 'string' ? x.localeCompare(y) : Number(x ?? -Infinity) - Number(y ?? -Infinity);
      return ordenRutas.asc ? cmp : -cmp;
    });
  }, [revenues, ordenRutas]);
  const ordenarRutas = (col: keyof RutaMetrica) => setOrdenRutas(o => (o.col === col ? { col, asc: !o.asc } : { col, asc: col === 'ruta' || col === 'cliente' }));
  const rutasPorUnidad = useMemo(() => rutas.filter(r => r.porUnidad !== null).sort((a, b) => (b.porUnidad ?? 0) - (a.porUnidad ?? 0)).slice(0, 8)
    .map(r => ({ key: r.key, label: r.ruta, value: Math.round(r.porUnidad ?? 0), color: SERIE.income })), [rutas]);
  const unidadRutas = rutas.map(r => r.unit).find(u => !!u) ?? 'unidad';

  const etiquetaPeriodo = periodo === 'mes'
    ? new Date(`${mes}-01T12:00:00`).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })
    : periodo === 'rango' ? `${desde} → ${hasta}` : (DATE_FILTERS.find(f => f.id === periodo)?.label ?? '');

  const totals = data?.period.totals;
  const negativo = totals ? totals.result.startsWith('-') : false;

  // Chips de los filtros activos: se ven y se quitan de un clic.
  const chips: { label: string; quitar: () => void }[] = [];
  if (vehicleId) chips.push({ label: `Vehículo ${vehicles.find(v => v.id === vehicleId)?.plate ?? ''}`, quitar: () => setVehicleId('') });
  if (subunitId) chips.push({ label: `Sub-unidad ${subunits.find(s => s.id === subunitId)?.name ?? ''}`, quitar: () => setSubunitId('') });
  if (customerId) chips.push({ label: `Cliente ${customers.find(c => c.id === customerId)?.name ?? ''}`, quitar: () => setCustomerId('') });

  const elegirMes = (key: string) => { setPeriodo('mes'); setMes(key); };

  // Reparto de gastos por categoría (con el color propio de cada categoría) e
  // ingresos por cliente (paleta categórica fija por orden de aparición).
  const repartoGastos = useMemo(() => plegar((data?.category.rows ?? []).map(r => ({ key: r.key, label: r.label, value: Number(r.expenses), color: colorDeCategoria(r.key) }))), [data]);
  const repartoClientes = useMemo(() => plegar((data?.customer.rows ?? []).map((r, i) => ({ key: r.key, label: r.label, value: Number(r.income), color: PALETA_CATEGORICA[i % PALETA_CATEGORICA.length] }))), [data]);

  // Tabla ordenable del desglose elegido.
  const filas = useMemo(() => {
    const rows = data?.[groupBy].rows ?? [];
    const num = (r: FinanceSummaryRow, c: Orden['col']) => Number((r[c as keyof FinanceSummaryRow] as string | number | undefined) ?? 0);
    return [...rows].sort((a, b) => {
      const cmp = orden.col === 'label' ? a.label.localeCompare(b.label) : num(a, orden.col) - num(b, orden.col);
      return orden.asc ? cmp : -cmp;
    });
  }, [data, groupBy, orden]);
  const maxFila = useMemo(() => Math.max(0, ...filas.map(r => Math.max(Number(r.income), Number(r.expenses)))), [filas]);
  const ordenar = (col: Orden['col']) => setOrden(o => (o.col === col ? { col, asc: !o.asc } : { col, asc: col === 'label' }));
  const filtrarDesdeFila = (r: FinanceSummaryRow) => {
    if (!r.key) return;
    if (groupBy === 'period') elegirMes(r.key);
    else if (groupBy === 'vehicle') setVehicleId(r.key);
    else if (groupBy === 'subunit') setSubunitId(r.key);
    else if (groupBy === 'customer') setCustomerId(r.key);
  };
  const filaClicable = groupBy !== 'category';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '0 1 180px', minWidth: '150px' }}>
            <Select label="Periodo" value={periodo} onChange={e => setPeriodo(e.target.value as Periodo)}
              options={[...DATE_FILTERS.map(f => ({ value: f.id, label: f.label })), { value: 'mes', label: 'Un mes concreto…' }, { value: 'rango', label: 'Rango de fechas…' }]} />
          </div>
          {periodo === 'mes' && (
            <div style={{ flex: '0 1 160px', minWidth: '150px' }}>
              <Input label="Mes" type="month" value={mes} onChange={e => setMes(e.target.value)} max={aISODate(new Date()).slice(0, 7)} />
            </div>
          )}
          {periodo === 'rango' && (
            <>
              <div style={{ flex: '0 1 150px', minWidth: '140px' }}>
                <Input label="Desde" type="date" value={rangoDesde} max={rangoHasta || undefined} onChange={e => setRangoDesde(e.target.value)} />
              </div>
              <div style={{ flex: '0 1 150px', minWidth: '140px' }}>
                <Input label="Hasta" type="date" value={rangoHasta} min={rangoDesde || undefined} onChange={e => setRangoHasta(e.target.value)} />
              </div>
            </>
          )}
          <div style={{ flex: '0 1 160px', minWidth: '140px' }}>
            <Select label="Vehículo" value={vehicleId} onChange={e => setVehicleId(e.target.value)}
              options={[{ value: '', label: 'Todos' }, ...vehicles.map(v => ({ value: v.id, label: v.plate }))]} />
          </div>
          {subunits.length > 0 && (
            <div style={{ flex: '0 1 170px', minWidth: '140px' }}>
              <Select label="Sub-unidad" value={subunitId} onChange={e => setSubunitId(e.target.value)}
                options={[{ value: '', label: 'Todas' }, ...subunits.map(s => ({ value: s.id, label: s.name }))]} />
            </div>
          )}
          <div style={{ flex: '0 1 170px', minWidth: '140px' }}>
            <Select label="Cliente" value={customerId} onChange={e => setCustomerId(e.target.value)}
              options={[{ value: '', label: 'Todos' }, ...customers.map(c => ({ value: c.id, label: c.active ? c.name : `${c.name} (inactivo)` }))]} />
          </div>
          <Button variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={reload} disabled={isLoading}>Actualizar</Button>
        </div>
        <ExportFinanceButtons desde={desde} hasta={hasta} subunitId={subunitId || undefined} compacto />
      </div>

      {chips.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>Filtrando por</span>
          {chips.map(c => <button key={c.label} type="button" className="finance-chip" onClick={c.quitar} title="Quitar filtro">{c.label} <X size={12} /></button>)}
          {customerId && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>(el cliente acota los ingresos; los gastos no tienen cliente)</span>}
        </div>
      )}

      {error && <LoadError what="el resultado financiero" error={error} />}
      {isLoading && !data && <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner size="lg" /></div>}

      {data && totals && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', opacity: isLoading ? 0.6 : 1, transition: 'opacity 150ms' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <Tile icon={<Banknote size={22} />} color={SERIE.income} label={`Ingresos · ${etiquetaPeriodo}`} valor={formatearCOP(totals.income)} hint={`${totals.revenueCount} ingreso${totals.revenueCount === 1 ? '' : 's'}${totals.quantity ? ` · ${formatearCantidad(totals.quantity, 'kg')}` : ''}`} />
            <Tile icon={<Receipt size={22} />} color={SERIE.vehicle} label="Gastos del vehículo" valor={formatearCOP(totals.expensesVehicle)} hint={porcentaje(totals.expensesVehicle, totals.income, 'de los ingresos')} />
            <Tile icon={<Wallet size={22} />} color={SERIE.operation} label="Gastos de operación" valor={formatearCOP(totals.expensesOperation)} hint={porcentaje(totals.expensesOperation, totals.income, 'de los ingresos')} />
            <Tile icon={negativo ? <TrendingDown size={22} /> : <TrendingUp size={22} />} color={negativo ? 'var(--critical)' : 'var(--accent)'} label="Resultado" valor={formatearResultado(totals.result)} hint={`margen ${porcentaje(totals.result, totals.income, '')} · ${totals.expenseCount} gasto${totals.expenseCount === 1 ? '' : 's'}`} />
          </div>

          <div className="finance-grid">
            <Card wide icon={<BarChart3 size={15} />} title="Tendencia mensual" hint="Clic en un mes para abrirlo" empty={data.period.rows.length === 0}>
              <TendenciaMensual rows={data.period.rows} onElegirMes={elegirMes} />
            </Card>

            <Card icon={<PieIcon size={15} />} title="En qué se va el gasto" hint="por categoría" empty={repartoGastos.length === 0}>
              <Reparto data={repartoGastos} centro={formatearCOP(totals.expenses)} centroLabel="gastos" />
            </Card>

            <Card icon={<Users size={15} />} title="De dónde viene el ingreso" hint="por cliente · clic para filtrar" empty={repartoClientes.length === 0}>
              <Reparto data={repartoClientes} centro={formatearCOP(totals.income)} centroLabel="ingresos" onElegir={k => setCustomerId(k)} />
            </Card>

            <Card icon={<Truck size={15} />} title="Resultado por vehículo" hint="sin la nómina · clic para filtrar" empty={data.vehicle.rows.length === 0}>
              <RankingResultado rows={data.vehicle.rows} onElegir={k => setVehicleId(k)} vacioLabel="Sin vehículo" />
            </Card>

            <Card icon={<Building2 size={15} />} title="Resultado por sub-unidad" hint="clic para filtrar" empty={data.subunit.rows.length === 0}>
              <RankingResultado rows={data.subunit.rows} onElegir={k => setSubunitId(k)} vacioLabel="Sin sub-unidad" />
            </Card>

            <Card wide icon={<Route size={15} />} title="Rutas más rentables" hint="lado del ingreso: lo que deja cada ruta por viaje y por unidad · clic en la ruta para ver sus ingresos" empty={rutas.length === 0}>
              <div className="finance-rutas">
                {rutasPorUnidad.length > 0 && (
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: '0 0 0.25rem', fontSize: '11.5px', color: 'var(--text-muted)' }}>Ingreso por {unidadRutas} (las 8 mejores)</p>
                    <RankedBarChart data={rutasPorUnidad} seriesName={`$ por ${unidadRutas}`} valueFormatter={pesosNum} />
                  </div>
                )}
                <div style={{ overflowX: 'auto', minWidth: 0 }}>
                  <table className="finance-table">
                    <thead>
                      <tr>
                        <ThR col="ruta" orden={ordenRutas} onClick={ordenarRutas}>Ruta</ThR>
                        <ThR col="viajes" orden={ordenRutas} onClick={ordenarRutas}>Viajes</ThR>
                        <ThR col="cantidad" orden={ordenRutas} onClick={ordenarRutas}>Cantidad</ThR>
                        <ThR col="ingresosNum" orden={ordenRutas} onClick={ordenarRutas}>Ingresos</ThR>
                        <ThR col="porViaje" orden={ordenRutas} onClick={ordenarRutas}>Por viaje</ThR>
                        <ThR col="porUnidad" orden={ordenRutas} onClick={ordenarRutas}>Por unidad</ThR>
                        <ThR col="ajustados" orden={ordenRutas} onClick={ordenarRutas}>Ajustados</ThR>
                      </tr>
                    </thead>
                    <tbody>
                      {rutas.map(r => (
                        <tr key={r.key} className="is-clickable" onClick={() => navigate(`${NAV_ROUTES.REVENUES}?customerId=${encodeURIComponent(r.clienteId)}&q=${encodeURIComponent(r.key.startsWith('sin-tarifa') ? '' : r.ruta)}&periodo=all`)} title="Ver los ingresos de esta ruta">
                          <td style={{ fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{r.ruta}<span style={{ display: 'block', fontSize: '10.5px', fontWeight: 500, color: 'var(--text-muted)' }}>{r.cliente}</span></td>
                          <td>{r.viajes}</td>
                          <td>{r.cantidad !== null ? formatearCantidad(String(r.cantidad), r.unit) : '—'}</td>
                          <td style={{ color: SERIE.income, fontWeight: 700 }}>{formatearCOP(r.ingresos)}</td>
                          <td>{pesosNum(Math.round(r.porViaje))}</td>
                          <td>{r.porUnidad !== null ? `${pesosNum(Math.round(r.porUnidad * 100) / 100)}${r.unit ? `/${r.unit}` : ''}` : '—'}</td>
                          <td style={{ color: r.ajustados ? 'var(--fin-vehicle)' : undefined }}>{r.ajustados ? `${r.ajustados} de ${r.viajes}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>

            <Card icon={<UserRound size={15} />} title="Gasto por conductor" hint="gastos registrados a nombre de un conductor" empty={porConductor.length === 0}>
              <RankedBarChart data={porConductor.map(d => ({ ...d, color: SERIE.vehicle }))} seriesName="Gasto" valueFormatter={pesosNum} />
            </Card>
          </div>

          <div className="finance-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="finance-card__head" style={{ padding: '0.875rem 1rem', marginBottom: 0, borderBottom: '1px solid var(--border)' }}>
              <h3 className="finance-card__title"><Scale size={15} style={{ color: 'var(--accent)' }} /> Detalle · {desde} → {hasta}</h3>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {groupBy === 'vehicle' && <span className="finance-card__hint">Por vehículo los gastos de operación no se restan.</span>}
                <div style={{ width: 200 }}>
                  <Select value={groupBy} onChange={e => { setGroupBy(e.target.value as FinanceGroupBy); setOrden({ col: 'label', asc: true }); }} options={FINANCE_GROUPS.map(g => ({ value: g.id, label: g.label }))} />
                </div>
              </div>
            </div>
            {filas.length === 0 ? (
              <p style={{ margin: 0, padding: '2rem', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>Sin movimientos en este periodo.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="finance-table">
                  <thead>
                    <tr>
                      <Th col="label" orden={orden} onClick={ordenar}>{FINANCE_GROUPS.find(g => g.id === groupBy)?.label.replace('Por ', '') ?? ''}</Th>
                      <th aria-label="comparación" style={{ cursor: 'default' }} />
                      <Th col="income" orden={orden} onClick={ordenar}>Ingresos</Th>
                      <Th col="expensesVehicle" orden={orden} onClick={ordenar}>G. vehículo</Th>
                      <Th col="expensesOperation" orden={orden} onClick={ordenar}>G. operación</Th>
                      <Th col="result" orden={orden} onClick={ordenar}>Resultado</Th>
                      <Th col="revenueCount" orden={orden} onClick={ordenar}>Mov.</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map(f => {
                      const neg = f.result.startsWith('-');
                      const clic = filaClicable && !!f.key;
                      return (
                        <tr key={f.key || f.label} className={clic ? 'is-clickable' : undefined} onClick={() => clic && filtrarDesdeFila(f)} title={clic ? 'Clic para filtrar por esta fila' : undefined}>
                          <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                            {f.key ? f.label : <span style={{ color: 'var(--text-muted)' }}>{groupBy === 'vehicle' ? 'Sin vehículo' : groupBy === 'subunit' ? 'Sin sub-unidad' : f.label}</span>}
                            {f.scope && <span style={{ marginLeft: '0.5rem', fontSize: '10.5px', fontWeight: 600, color: f.scope === 'OPERATION' ? SERIE.operation : 'var(--text-muted)' }}>{f.scope === 'OPERATION' ? 'operación' : 'vehículo'}</span>}
                            {f.quantity && <span style={{ marginLeft: '0.5rem', fontSize: '10.5px', color: 'var(--text-muted)' }}>{formatearCantidad(f.quantity, 'kg')}</span>}
                          </td>
                          <td><BarraComparada ingresos={Number(f.income)} gastos={Number(f.expenses)} max={maxFila} /></td>
                          <td style={{ color: SERIE.income, fontWeight: 600 }}>{formatearCOP(f.income)}</td>
                          <td>{formatearCOP(f.expensesVehicle)}</td>
                          <td style={{ opacity: groupBy === 'vehicle' ? 0.5 : 1 }}>{formatearCOP(f.expensesOperation)}</td>
                          <td style={{ fontWeight: 800, color: neg ? 'var(--critical)' : 'var(--text-primary)' }}>{formatearResultado(f.result)}</td>
                          <td style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{f.revenueCount} ing. · {f.expenseCount} g.</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td />
                      <td style={{ color: SERIE.income }}>{formatearCOP(totals.income)}</td>
                      <td>{formatearCOP(totals.expensesVehicle)}</td>
                      <td>{formatearCOP(totals.expensesOperation)}</td>
                      <td style={{ color: negativo ? 'var(--critical)' : undefined }}>{formatearResultado(totals.result)}</td>
                      <td style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{totals.revenueCount} ing. · {totals.expenseCount} g.</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '13px', alignItems: 'center' }}>
            <Link to={NAV_ROUTES.REVENUES} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' }}>Ingresos <ArrowRight size={14} /></Link>
            <Link to={NAV_ROUTES.EXPENSES} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' }}>Gastos <ArrowRight size={14} /></Link>
            {puedeGestionarTarifas(user) && (
              <Link to={NAV_ROUTES.ADMIN} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' }}>Tarifas y clientes <ArrowRight size={14} /></Link>
            )}
            <span style={{ marginLeft: 'auto', fontSize: '11.5px', color: 'var(--text-muted)' }}>Cifras de los agregados del servidor, las mismas del XLSX. Días de Colombia.</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** «47 % de los ingresos»; vacío si no hay base. */
function porcentaje(parte: string, base: string, sufijo: string): string {
  const b = Number(base);
  if (!b) return '';
  const p = (Number(parte) / b) * 100;
  return `${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(p)} %${sufijo ? ` ${sufijo}` : ''}`;
}

function Card({ title, icon, hint, empty = false, wide = false, children }: { title: string; icon: React.ReactNode; hint?: string; empty?: boolean; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={`finance-card${wide ? ' finance-card--wide' : ''}`}>
      <div className="finance-card__head">
        <h3 className="finance-card__title"><span style={{ color: 'var(--accent)', display: 'flex' }}>{icon}</span>{title}</h3>
        {hint && <span className="finance-card__hint">{hint}</span>}
      </div>
      {empty ? <p style={{ margin: 0, padding: '2rem 0', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>Sin datos en este periodo.</p> : children}
    </div>
  );
}

function ThR({ col, orden, onClick, children }: { col: keyof RutaMetrica; orden: { col: keyof RutaMetrica; asc: boolean }; onClick: (c: keyof RutaMetrica) => void; children: React.ReactNode }) {
  const activa = orden.col === col;
  return (
    <th className={activa ? 'is-sorted' : undefined} onClick={() => onClick(col)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>{children}{activa && (orden.asc ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}</span>
    </th>
  );
}

function Th({ col, orden, onClick, children }: { col: Orden['col']; orden: Orden; onClick: (c: Orden['col']) => void; children: React.ReactNode }) {
  const activa = orden.col === col;
  return (
    <th className={activa ? 'is-sorted' : undefined} onClick={() => onClick(col)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>{children}{activa && (orden.asc ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}</span>
    </th>
  );
}

function Tile({ icon, color, label, valor, hint }: { icon: React.ReactNode; color: string; label: string; valor: string; hint?: string }) {
  return (
    <div style={{ background: 'var(--bg-surface)', padding: '1.125rem 1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
      <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'var(--bg-raised)', color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <span style={{ fontSize: '19px', fontWeight: 800, color: 'var(--text-primary)', display: 'block', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis' }}>{valor}</span>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block' }}>{label}</span>
        {hint && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{hint}</span>}
      </div>
    </div>
  );
}
